import { z } from 'zod';
import { PERMISSIONS } from '../constants/permissions.js';
import { email, password, pin, objectId, text } from './common.js';

const anyPassword = z.string().min(1, 'Required').max(128);
const linkToken = z.string().trim().min(20).max(200);

export const loginSchema = z.strictObject({ email, password: anyPassword });
export const forgotPasswordSchema = z.strictObject({ email });
export const resetPasswordSchema = z.strictObject({ token: linkToken, password });
export const setPasswordSchema = resetPasswordSchema;
export const changePasswordSchema = z
  .strictObject({ currentPassword: anyPassword, newPassword: password })
  .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'New password must be different',
    path: ['newPassword'],
  });

export const pinLoginSchema = z.strictObject({ userId: objectId, pin });
export const managerOverrideSchema = z.strictObject({
  userId: objectId,
  pin,
  permission: z.enum(PERMISSIONS),
});
export const selectOutletSchema = z.strictObject({ outletId: objectId });

/** Activation code: 8 Crockford base32 chars, shown as XXXX-XXXX. */
export const activationCodeInput = z
  .string()
  .trim()
  .toUpperCase()
  .transform((v) => v.replace(/[^0-9A-Z]/g, ''))
  .pipe(z.string().regex(/^[0-9A-HJKMNP-TV-Z]{8}$/, 'Enter the 8-character code'));

export const activateDeviceSchema = z.strictObject({
  code: activationCodeInput,
  name: z.string().trim().min(1, 'Required').max(60),
  info: z
    .strictObject({
      platform: text(60).optional(),
      os: text(60).optional(),
      browser: text(60).optional(),
      model: text(80).optional(),
      screen: text(30).optional(),
    })
    .optional(),
});

// ---- Admin realm ----
export const adminLoginSchema = loginSchema;
export const mfaTokenSchema = z.strictObject({ mfaToken: z.string().min(20).max(2000) });
export const mfaConfirmSchema = z.strictObject({
  mfaToken: z.string().min(20).max(2000),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
export const mfaVerifySchema = z
  .strictObject({
    mfaToken: z.string().min(20).max(2000),
    code: z
      .string()
      .trim()
      .regex(/^\d{6}$/, 'Enter the 6-digit code')
      .optional(),
    recoveryCode: z.string().trim().min(8).max(20).optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), {
    message: 'Enter either an authenticator code or a recovery code',
    path: ['code'],
  });
